import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lesson, CourseWithLessons, LessonWithProgress } from '../services/courseService';
import { mockCourseService } from '../services/mockData';

// Interactive Component for drag-and-drop exercises
const DragDropClassification: React.FC<{
  scenarios: any[];
  onComplete: (score: number) => void;
}> = ({ scenarios, onComplete }) => {
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [categories] = useState(['supervised', 'unsupervised', 'reinforcement']);
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [score, setScore] = useState(0);

  const handleDragStart = (e: React.DragEvent, item: any) => {
    setDraggedItem(item);
  };

  const handleDrop = (e: React.DragEvent, category: string) => {
    e.preventDefault();
    if (!draggedItem) return;

    const isCorrect = draggedItem.answer === category;
    const newCompleted = { ...completed, [draggedItem.text]: true };
    const newFeedback = { ...feedback, [draggedItem.text]: draggedItem.explanation };
    
    setCompleted(newCompleted);
    setFeedback(newFeedback);
    
    if (isCorrect) {
      setScore(score + 1);
    }

    // Check if all scenarios are completed
    if (Object.keys(newCompleted).length === scenarios.length) {
      setTimeout(() => onComplete((score + (isCorrect ? 1 : 0)) / scenarios.length * 100), 1000);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Instructions</h4>
        <p className="text-blue-800 text-sm">
          Drag each scenario to the correct machine learning type. You'll get immediate feedback!
        </p>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => (
          <div
            key={category}
            className="min-h-24 sm:min-h-32 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
            onDrop={(e) => handleDrop(e, category)}
            onDragOver={handleDragOver}
          >
            <h4 className="font-medium text-center text-gray-900 capitalize mb-2 text-sm sm:text-base">
              {category.replace('-', ' ')} Learning
            </h4>
            <div className="text-xs text-gray-600 text-center">
              Drop scenarios here
            </div>
          </div>
        ))}
      </div>

      {/* Scenarios */}
      <div className="space-y-3">
        {scenarios.map((scenario, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg cursor-move transition-all ${
              completed[scenario.text] 
                ? (scenario.answer === draggedItem?.answer ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300')
                : 'bg-white border border-gray-200 hover:border-indigo-300'
            }`}
            draggable={!completed[scenario.text]}
            onDragStart={(e) => handleDragStart(e, scenario)}
          >
            <p className="text-gray-900">{scenario.text}</p>
            {feedback[scenario.text] && (
              <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-800">
                💡 {feedback[scenario.text]}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Progress */}
      <div className="text-center text-gray-600">
        Completed: {Object.keys(completed).length} / {scenarios.length}
      </div>
    </div>
  );
};

// Quiz Component
const QuizComponent: React.FC<{
  questions: any[];
  onComplete: (score: number) => void;
}> = ({ questions, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (showFeedback) return;

    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);
    setShowFeedback(true);

    if (answerIndex === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
  };

  const handleNext = () => {
    setShowFeedback(false);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setCompleted(true);
      onComplete((score / questions.length) * 100);
    }
  };

  if (completed) {
    return (
      <div className="text-center space-y-4">
        <div className="text-6xl">🎉</div>
        <h3 className="text-2xl font-bold text-gray-900">Quiz Complete!</h3>
        <p className="text-lg text-gray-600">
          You scored {score} out of {questions.length} ({Math.round((score / questions.length) * 100)}%)
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(score / questions.length) * 100}%` }}
          ></div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const isCorrect = selectedAnswers[currentQuestion] === question.correctAnswer;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Question {currentQuestion + 1} of {questions.length}
        </h3>
        <div className="text-sm text-gray-600">
          Score: {score}/{questions.length}
        </div>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div 
          className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentQuestion + (showFeedback ? 1 : 0)) / questions.length) * 100}%` }}
        ></div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">{question.question}</h4>
        
        <div className="space-y-3">
          {question.options.map((option: string, index: number) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              disabled={showFeedback}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${
                showFeedback
                  ? index === question.correctAnswer
                    ? 'border-green-500 bg-green-50'
                    : index === selectedAnswers[currentQuestion]
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-gray-50'
                  : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
              }`}
            >
              <div className="flex items-center">
                <span className={`mr-3 w-6 h-6 rounded-full border flex items-center justify-center text-sm ${
                  showFeedback && index === question.correctAnswer
                    ? 'bg-green-500 border-green-500 text-white'
                    : showFeedback && index === selectedAnswers[currentQuestion] && !isCorrect
                    ? 'bg-red-500 border-red-500 text-white'
                    : 'border-gray-400'
                }`}>
                  {showFeedback && index === question.correctAnswer ? '✓' : String.fromCharCode(65 + index)}
                </span>
                {option}
              </div>
            </button>
          ))}
        </div>

        {showFeedback && (
          <div className={`mt-4 p-4 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className={`font-medium mb-2 ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
              {isCorrect ? '✅ Correct!' : '❌ Incorrect'}
            </div>
            <p className={`text-sm ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
              {question.explanation}
            </p>
          </div>
        )}
      </div>

      {showFeedback && (
        <div className="text-center">
          <button
            onClick={handleNext}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            {currentQuestion < questions.length - 1 ? 'Next Question' : 'Complete Quiz'}
          </button>
        </div>
      )}
    </div>
  );
};

// Coding Exercise Component
const CodingExercise: React.FC<{
  lesson: Lesson;
  onComplete: (score: number) => void;
}> = ({ lesson, onComplete }) => {
  const [code, setCode] = useState(`# Movie Rating Prediction
import pandas as pd
from sklearn.linear_model import LinearRegression
from sklearn.model_selection import train_test_split

# Load the movie dataset
movies = pd.read_csv('movies.csv')

# TODO: Select features for prediction
# Hint: Use budget, runtime, and genre_encoded columns
X = movies[['budget', 'runtime']]  # Add more features!
y = movies['rating']

# Split the data
X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2)

# TODO: Create and train the model
model = LinearRegression()
# Your code here...

print("Model training complete!")
`);
  const [output, setOutput] = useState('');
  const [testsPass, setTestsPass] = useState(0);
  const [totalTests] = useState(3);
  const [hasRun, setHasRun] = useState(false);

  const runCode = () => {
    setHasRun(true);
    // Simulate code execution
    const codeLines = code.split('\n');
    let passedTests = 0;
    let simulatedOutput = 'Running your code...\n\n';

    // Check for model creation
    if (code.includes('LinearRegression()') && code.includes('model =')) {
      passedTests++;
      simulatedOutput += '✅ Test 1 Passed: Model created successfully\n';
    } else {
      simulatedOutput += '❌ Test 1 Failed: Create a LinearRegression model\n';
    }

    // Check for model training
    if (code.includes('fit(') || code.includes('.fit')) {
      passedTests++;
      simulatedOutput += '✅ Test 2 Passed: Model training code found\n';
    } else {
      simulatedOutput += '❌ Test 2 Failed: Train the model using .fit()\n';
    }

    // Check for additional features
    if (code.includes('genre_encoded') || code.includes("'director_experience'")) {
      passedTests++;
      simulatedOutput += '✅ Test 3 Passed: Additional features included\n';
    } else {
      simulatedOutput += '❌ Test 3 Failed: Try adding more features (genre_encoded, director_experience)\n';
    }

    simulatedOutput += '\nModel training complete!\n';
    simulatedOutput += `Accuracy: ${65 + (passedTests * 10)}%\n`;
    simulatedOutput += `Mean Squared Error: ${2.1 - (passedTests * 0.3)}\n\n`;

    if (passedTests === totalTests) {
      simulatedOutput += '🎉 Congratulations! All tests passed!\n';
      simulatedOutput += 'Your model is ready for predictions!';
    } else {
      simulatedOutput += `💡 ${totalTests - passedTests} test(s) still need work. Keep coding!`;
    }

    setOutput(simulatedOutput);
    setTestsPass(passedTests);

    if (passedTests === totalTests) {
      setTimeout(() => onComplete(100), 1500);
    }
  };

  return (
    <div className="space-y-6">
      {/* Instructions - Always on top on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4 order-1 lg:order-1">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">📝 Your Task</h4>
            <p className="text-blue-800 text-sm mb-2">
              Complete the movie rating prediction model by:
            </p>
            <ol className="text-blue-800 text-sm list-decimal list-inside space-y-1">
              <li>Adding more features to improve accuracy</li>
              <li>Training the LinearRegression model</li>
              <li>Getting all tests to pass</li>
            </ol>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-medium text-yellow-900 mb-2">💡 Hints</h4>
            <ul className="text-yellow-800 text-sm space-y-1">
              <li>• Use model.fit(X_train, y_train) to train</li>
              <li>• Try adding 'genre_encoded' to your features</li>
              <li>• Director experience might predict rating</li>
            </ul>
          </div>

          {/* Test Results */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Test Results</h4>
              <span className={`text-sm ${testsPass === totalTests ? 'text-green-600' : 'text-gray-600'}`}>
                {testsPass}/{totalTests} Passed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(testsPass / totalTests) * 100}%` }}
              ></div>
            </div>
          </div>

          <button
            onClick={runCode}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            ▶️ Run Code
          </button>
        </div>

        {/* Code Editor and Output */}
        <div className="space-y-4 order-2 lg:order-2">
          <div className="bg-gray-900 rounded-lg p-4 h-64 lg:h-80 overflow-auto">
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full h-full bg-transparent text-green-400 font-mono text-sm resize-none outline-none"
              spellCheck={false}
              style={{ fontFamily: 'Monaco, Consolas, monospace' }}
              placeholder="Write your code here..."
            />
          </div>

          {/* Output Panel */}
          {hasRun && (
            <div className="bg-black rounded-lg p-4 h-32 overflow-auto">
              <pre className="text-white text-sm font-mono whitespace-pre-wrap">
                {output}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const LessonViewerPage: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseWithLessons | null>(null);
  const [lessonsWithProgress, setLessonsWithProgress] = useState<LessonWithProgress[]>([]);
  const [currentLesson, setCurrentLesson] = useState<LessonWithProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [interactiveScore, setInteractiveScore] = useState<number | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!courseId || !lessonId) return;

      try {
        setLoading(true);
        const [courseData, lessonsData] = await Promise.all([
          mockCourseService.getCourse(courseId),
          mockCourseService.getCourseLessonsWithProgress(courseId)
        ]);

        setCourse(courseData);
        setLessonsWithProgress(lessonsData);
        
        const lesson = lessonsData.find(l => l.id === lessonId);
        setCurrentLesson(lesson || null);
        setLessonCompleted(lesson?.is_completed || false);
      } catch (err) {
        console.error('Failed to load lesson data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [courseId, lessonId]);

  const handleMarkComplete = async () => {
    if (!lessonId) return;
    
    try {
      await mockCourseService.updateLessonProgress(lessonId, { completed: true });
      setLessonCompleted(true);
      
      // Update the lessons progress
      const updatedLessons = lessonsWithProgress.map(lesson => 
        lesson.id === lessonId 
          ? { ...lesson, is_completed: true, completion_percentage: 100 }
          : lesson
      );
      setLessonsWithProgress(updatedLessons);
    } catch (err) {
      console.error('Failed to mark lesson complete:', err);
    }
  };

  const handleInteractiveComplete = (score: number) => {
    setInteractiveScore(score);
    setLessonCompleted(true);
  };

  const navigateToLesson = (newLessonId: string) => {
    navigate(`/courses/${courseId}/lessons/${newLessonId}`);
  };

  const getCurrentLessonIndex = () => {
    return lessonsWithProgress.findIndex(l => l.id === lessonId);
  };

  const getNextLesson = () => {
    const currentIndex = getCurrentLessonIndex();
    return currentIndex < lessonsWithProgress.length - 1 
      ? lessonsWithProgress[currentIndex + 1] 
      : null;
  };

  const getPreviousLesson = () => {
    const currentIndex = getCurrentLessonIndex();
    return currentIndex > 0 
      ? lessonsWithProgress[currentIndex - 1] 
      : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!course || !currentLesson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Lesson not found</h1>
          <Link to={`/courses/${courseId}`} className="text-indigo-600 hover:text-indigo-500">
            ← Back to Course
          </Link>
        </div>
      </div>
    );
  }

  const currentIndex = getCurrentLessonIndex();
  const progress = ((currentIndex + 1) / lessonsWithProgress.length) * 100;
  const nextLesson = getNextLesson();
  const previousLesson = getPreviousLesson();

  const renderLessonContent = () => {
    switch (currentLesson.lesson_type) {
      case 'text':
        return (
          <div className="prose max-w-none">
            <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
              {currentLesson.content.split('\n').map((line, index) => {
                if (line.startsWith('# ')) {
                  return <h1 key={index} className="text-3xl font-bold text-gray-900 mt-8 mb-4">{line.substring(2)}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={index} className="text-2xl font-bold text-gray-900 mt-6 mb-3">{line.substring(3)}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-xl font-bold text-gray-900 mt-4 mb-2">{line.substring(4)}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <div key={index} className="font-bold text-gray-900 mt-3 mb-2">{line.substring(2, line.length - 2)}</div>;
                } else if (line.startsWith('- ')) {
                  return <div key={index} className="ml-4 mb-1">• {line.substring(2)}</div>;
                } else if (line.trim() === '') {
                  return <div key={index} className="mb-3"></div>;
                } else if (line.startsWith('```')) {
                  return null; // Handle code blocks separately if needed
                } else {
                  return <p key={index} className="mb-3">{line}</p>;
                }
              })}
            </div>
          </div>
        );

      case 'interactive':
        if (currentLesson.resources?.interactiveType === 'drag-drop-classification') {
          const scenarios = Array.isArray(currentLesson.resources.scenarios) 
            ? currentLesson.resources.scenarios 
            : [];
          return (
            <DragDropClassification
              scenarios={scenarios}
              onComplete={handleInteractiveComplete}
            />
          );
        }
        return <div className="text-gray-600">Interactive content coming soon!</div>;

      case 'quiz':
        const questions = Array.isArray(currentLesson.resources?.questions) 
          ? currentLesson.resources.questions 
          : [];
        return (
          <QuizComponent
            questions={questions}
            onComplete={handleInteractiveComplete}
          />
        );

      case 'coding':
        return (
          <CodingExercise
            lesson={currentLesson}
            onComplete={handleInteractiveComplete}
          />
        );

      default:
        return (
          <div className="prose max-w-none">
            <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
              {currentLesson.content}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="w-full bg-gray-200 h-1">
          <div 
            className="bg-indigo-600 h-1 transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <Link to={`/courses/${courseId}`} className="text-indigo-600 hover:text-indigo-500 text-sm">
                ← {course.title}
              </Link>
              <h1 className="text-xl font-bold text-gray-900 mt-1">{currentLesson.title}</h1>
            </div>
            <div className="text-sm text-gray-600">
              Lesson {currentIndex + 1} of {lessonsWithProgress.length}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Mobile Sidebar Toggle */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-500 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span>Course Content ({lessonsWithProgress.findIndex(l => l.id === lessonId) + 1}/{lessonsWithProgress.length})</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Lesson List */}
          <div className="lg:col-span-1">
            <div className={`bg-white rounded-lg shadow-sm p-4 lg:sticky lg:top-24 ${
              isSidebarOpen ? 'block' : 'hidden lg:block'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">Course Content</h3>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="lg:hidden text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {lessonsWithProgress.map((lesson, index) => (
                  <button
                    key={lesson.id}
                    onClick={() => {
                      navigateToLesson(lesson.id);
                      setIsSidebarOpen(false);
                    }}
                    className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${
                      lesson.id === lessonId 
                        ? 'bg-indigo-100 border border-indigo-300' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        lesson.is_completed
                          ? 'bg-green-100 text-green-600'
                          : lesson.id === lessonId
                          ? 'bg-indigo-100 text-indigo-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {lesson.is_completed ? '✓' : index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{lesson.title}</div>
                        <div className="text-gray-500 text-xs">
                          {lesson.lesson_type} • {lesson.estimated_duration_minutes} min
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-8">
              {/* Lesson Header */}
              <div className="mb-8">
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center">
                    <span className="mr-1">📚</span>
                    {currentLesson.lesson_type}
                  </span>
                  <span className="flex items-center">
                    <span className="mr-1">⏱️</span>
                    {currentLesson.estimated_duration_minutes} minutes
                  </span>
                  <span className="flex items-center">
                    <span className="mr-1">⭐</span>
                    {currentLesson.points_value} points
                  </span>
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{currentLesson.title}</h1>
                <p className="text-lg text-gray-600">{currentLesson.description}</p>
              </div>

              {/* Lesson Content */}
              <div className="mb-8">
                {renderLessonContent()}
              </div>

              {/* Completion Status */}
              {interactiveScore !== null && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">✅</span>
                    <span className="font-medium text-green-800">
                      Exercise completed! Score: {Math.round(interactiveScore)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-6 border-t border-gray-200 space-y-4 sm:space-y-0">
                <div>
                  {previousLesson ? (
                    <button
                      onClick={() => navigateToLesson(previousLesson.id)}
                      className="flex items-center text-indigo-600 hover:text-indigo-500"
                    >
                      <span className="mr-1">←</span>
                      <span className="hidden sm:inline">Previous: {previousLesson.title}</span>
                      <span className="sm:hidden">Previous</span>
                    </button>
                  ) : (
                    <div></div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  {!lessonCompleted && currentLesson.lesson_type === 'text' && (
                    <button
                      onClick={handleMarkComplete}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-center"
                    >
                      Mark as Complete
                    </button>
                  )}

                  {(lessonCompleted || interactiveScore !== null) && nextLesson && (
                    <button
                      onClick={() => navigateToLesson(nextLesson.id)}
                      className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                    >
                      <span className="hidden sm:inline">Next: {nextLesson.title}</span>
                      <span className="sm:hidden">Next Lesson</span>
                      <span className="ml-1">→</span>
                    </button>
                  )}

                  {(lessonCompleted || interactiveScore !== null) && !nextLesson && (
                    <Link
                      to={`/courses/${courseId}`}
                      className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-center"
                    >
                      Course Complete! 🎉
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};